import { Router } from '@angular/router';
import { ITokenModel } from '@delon/auth';

import { BaseLogic } from './base.logic';
import { MenuLogic } from './menu.logic';

import { CookieService } from '@delon/util';
import { IApplicationData, IKVPaire } from '@shared/model';
import { REDIRECT_URI } from 'src/app/shared/consts/cache.consts';
import { isWorkWeixinBrowser } from '@shared';

const VALID_APP_TYPES = [1, 3, 4, 5, 6];
const APP_TYPE_BY_ROUTE: Record<string, number> = {
  admin: 1,
  hospital: 6,
  pharmacy: 3,
  distributor: 5
};

/**
 * 根据失效时仍可获得的上下文解析登录应用类型。
 *
 * 入参：显式应用类型、Token 数据、缓存应用类型和当前路由路径。
 * 出参：有效的应用类型编码；无上下文时返回药店端兼容默认值 `3`。
 * 示例：`resolveLoginAppType(null, { app_type: 6 } as ITokenModel, null, '/pharmacy/home')` 返回 `6`。
 */
export function resolveLoginAppType(
  explicitAppType: unknown,
  tokenData: ITokenModel | null | undefined,
  cachedAppType: unknown,
  currentPath: string
): number {
  const explicit = normalizeAppType(explicitAppType);
  if (explicit) return explicit;

  const tokenAppType = normalizeAppType(tokenData?.['app_type'] ?? tokenData?.['appType']);
  if (tokenAppType) return tokenAppType;

  const routeName = currentPath.split('#').pop()?.split('?')[0].split('/').filter(Boolean)[0] || '';
  const routeAppType = APP_TYPE_BY_ROUTE[routeName];
  if (routeAppType) return routeAppType;

  const cached = normalizeAppType(cachedAppType);
  return cached || 3;
}

/**
 * 将任意输入收敛为项目支持的应用类型编码。
 *
 * 入参：待校验的应用类型值。
 * 出参：有效编码，或无效值对应的 `0`。
 * 示例：`normalizeAppType('6')` 返回 `6`。
 */
function normalizeAppType(value: unknown): number {
  const appType = Number(value || 0);
  return VALID_APP_TYPES.includes(appType) ? appType : 0;
}

export class ApplicationLogic extends BaseLogic {
  /**
   * 加载应用信息
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-04
   * @param {ITokenModel} tokenData token数据
   * @param {IApplicationData} [appData=null] 应用数据
   */
  load(tokenData: ITokenModel, appData: IApplicationData | null = null): void {
    const tokenRecord = tokenData as ITokenModel & Record<string, unknown>;
    const appType = this.normalizeAppType(
      appData?.appType ?? tokenRecord['app_type'] ?? tokenRecord['appType'] ?? this.privilegeSrv.appType
    );
    if (appType) {
      tokenRecord['app_type'] = appType;
      this.privilegeSrv.appType = appType;
    }
    // 设置token
    this.tokenSrv.set(tokenRecord);
    // 设置cookie的token
    this.injector.get<CookieService>(CookieService).put('token', tokenData.token as string, {
      expires: tokenData.expired
    });
    if (null != appData) {
      let { user, functions, menu, appType }: IApplicationData = appData;
      // 保存用户信息
      if (user) this.privilegeSrv.user = user;
      // 保存菜单信息
      if (menu) this.privilegeSrv.menu = menu;
      // 保存功能编码信息
      if (functions) this.privilegeSrv.functions = functions;
      // 保存应用类型
      const normalizedAppType = this.normalizeAppType(appType);
      if (normalizedAppType) this.privilegeSrv.appType = normalizedAppType;
    } else {
      this.privilegeSrv.refresh();
    }
    const user = this.privilegeSrv.user;
    // 加载用户信息
    if (user) {
      this.settingsSrv.setUser({
        name: user.realname,
        avatar: user.img_head,
        ...user
      });
    }
    // if (1 == this.privilegeSrv.appType) {
    // }
    // this.menuSrv.add(this.privilegeSrv.menu);
    const functions = this.privilegeSrv.functions;
    // 配置权限信息。刷新token时若本地权限缓存已过期，保留当前ACL，避免按钮权限被清空。
    if (functions || null != appData) {
      this.aclSrv.setAbility(this.privilegeSrv.funcode);
    }
    const menu = this.privilegeSrv.menu;
    setTimeout(() => {
      // 加载菜单信息。刷新token时若本地菜单缓存已过期，保留当前菜单，避免左侧菜单被清空。
      if (!menu || Object.keys(menu).length <= 0) return;
      this.menuSrv.clear();
      this.menuSrv.add(MenuLogic.getInstance(this.injector).load(menu));
    }, 10);
  }

  private normalizeAppType(value: unknown): number {
    const appType = Number(value || 0);
    return VALID_APP_TYPES.includes(appType) ? appType : 0;
  }

  /**
   * 跳转至登录页面
   *
   * @author 王阮强(wangruanqiang@youzhibo.cn)
   * @date 2021-02-02
   * @private
   * @param {string} [content=null] 提示框详细文本
   * @param {string} [title=null] 提示框标题
   * @param {number} [appType=null] 跳转应用类型
   */
  toLogin(
    content: string | null = null,
    title: string | null = null,
    appType: number | null = null,
    queryParams: IKVPaire | null = null,
    cacheRedirect = true
  ): void {
    const tokenData = (this.tokenSrv.get() || null) as ITokenModel | null;
    const path = this.getCurrentPath();
    const resolvedAppType = resolveLoginAppType(appType, tokenData, this.privilegeSrv.appType, path);

    this.privilegeSrv.clear();
    this.menuSrv.clear();
    this.aclSrv.setAbility([]);
    // 页面提示
    if (null !== content && null !== title) {
      this.notificationSrv.error(title, content);
    }
    // 根据当前应用类型决定跳转登录页面地址
    appType = resolvedAppType;
    this.privilegeSrv.appType = appType;
    const appTypeName = this.privilegeSrv.getAppTypeName(appType);
    // 判断企微打开页面时，登录页固定添加workweixin节点
    // 即 /{appName}/workweixin/passport/login 固定格式
    let workweixin = '';
    if (isWorkWeixinBrowser()) {
      workweixin = '/workweixin';
    }
    // 登录页面判断
    const loginUrl = this.privilegeSrv.loginUrl ? this.privilegeSrv.loginUrl : `/${appTypeName}${workweixin}${this.tokenSrv.login_url}`;
    this.tokenSrv.clear();
    this.injector.get(CookieService).removeAll();
    // 判断当前页面是否登录页，如果是则无需重复跳转
    // 登录页或者根目录 / 无需跳转
    if (path.indexOf('/passport/login') >= 0 || '/' == path || path.indexOf('/wxwork/wxwork') >= 0) {
      return;
    }
    // 缓存跳转地址
    if (!cacheRedirect) {
      this.cacheSrv.remove(REDIRECT_URI);
      this.goTo(loginUrl, queryParams);
      return;
    }
    this.cacheSrv.set(REDIRECT_URI, decodeURIComponent(path), {
      type: 's',
      expire: 600
    });
    this.goTo(loginUrl, {
      redirect_uri: encodeURIComponent(path),
      ...(queryParams || {})
    });
  }

  /**
   * 读取当前 Angular 路由路径，兼容 HashLocationStrategy 和 PathLocationStrategy。
   *
   * 入参：无，读取浏览器当前地址。
   * 出参：不含 URL 主机部分的应用路由路径。
   * 示例：当前地址为 `#/hospital/home` 时返回 `/hospital/home`。
   */
  private getCurrentPath(): string {
    const url = new URL(location.href);
    return url.hash ? url.hash.substring(1) : url.pathname;
  }
  /**
   * 跳转首页
   */
  toHome() {
    // 获取当前模块名
    const module = this.privilegeSrv.appTypeName,
      // 获取当前本地是否有缓存重定向地址
      redirectUri = this.cacheSrv.get(REDIRECT_URI, {
        mode: 'none'
      });
    this.cacheSrv.remove(REDIRECT_URI);
    this.goTo(redirectUri || module);
  }
  /**
   * 跳转页面
   */
  /**
   * 跳转指定页面
   *
   * @param {string} path 跳转地址，相对路径
   * @param {(IKVPaire | null)} [queryParams=null] 跳转queryString参数
   */
  goTo(path: string, queryParams: IKVPaire | null = null): void {
    setTimeout(() => {
      this.injector.get(Router).navigate(
        [path],
        queryParams
          ? {
              queryParams
            }
          : undefined
      );
    });
  }
  /**
   * 应用程序初始化，处理token等信息
   */
  init(appType: number, loginUrl: string | null = null, appData: Partial<IApplicationData> | null = null): void {
    // 配置登录地址
    this.privilegeSrv.loginUrl = loginUrl;
    const tokenData = (this.tokenSrv.get() || {}) as ITokenModel;
    const hasValidToken = Object.keys(tokenData).length > 0 && !!tokenData.expired && tokenData.expired >= +new Date();
    const currentAppType = this.privilegeSrv.appType;
    if (currentAppType && appType !== currentAppType) {
      return this.toLogin(null, null, appType, null, false);
    }
    // 验证访问的应用和权限中的应用不一致，则跳转登录
    if (appType != this.privilegeSrv.appType) {
      if (hasValidToken) {
        this.privilegeSrv.appType = appType;
      } else {
        return this.toLogin(null, null, appType);
      }
    }
    if (hasValidToken && !this.privilegeSrv.appType) {
      this.privilegeSrv.appType = appType;
    }
    // 判断本地token是否有效
    if (Object.keys(tokenData).length > 0) {
      if (!tokenData.expired || tokenData.expired < +new Date()) {
        return this.toLogin('登录令牌已过期', '登陆失效', appType);
      }
    } else {
      return this.toLogin(null, null, appType);
    }
    // token有效，则使用token数据重新初始化
    return this.load(
      tokenData,
      appData
        ? {
            user: appData.user || this.privilegeSrv.user,
            functions: appData.functions || this.privilegeSrv.functions,
            menu: appData.menu || this.privilegeSrv.menu,
            appType: appData.appType || appType
          }
        : null
    );
  }
}
